import type { ReactNode } from 'react';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import { BookCover } from '@/components/BookCover/BookCover';
import { getBooks, deleteBook } from '@/api/books';
import type { Book, ListBooksStats, Placement, ReadStatus, Series } from '@/api/types';
import { SeriesAutocomplete } from '@/components/EntityAutocomplete/SeriesAutocomplete';
import { ListPagination } from '@/components/ListPagination/ListPagination';
import styles from './BookListPage.module.css';

const PLACEMENT_LABELS: Record<string, string> = {
  collection: 'Коллекция',
  wishlist: 'Вишлист',
  in_transit: 'В пути',
};

const READ_STATUS_LABELS: Record<string, string> = {
  not_started: 'Не начата',
  reading: 'Читаю',
  finished: 'Прочитана',
};

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 24;

function booksCountPhrase(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word: string;
  if (mod10 === 1 && mod100 !== 11) word = 'книга';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) word = 'книги';
  else word = 'книг';
  return `${n} ${word}`;
}

export function BookListPage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<{
    items: Book[];
    total: number;
    stats: ListBooksStats;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [placementFilter, setPlacementFilter] = useState<string>('');
  const [readStatusFilter, setReadStatusFilter] = useState<string>('');
  const [seriesFilter, setSeriesFilter] = useState<Series | null>(null);
  const requestIdRef = useRef(0);
  const didInitFromParamsRef = useRef(false);
  const prevDebouncedSearchRef = useRef<string | null>(null);

  const page = useMemo(
    () => Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1),
    [searchParams],
  );

  const clearPageFromUrl = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (!next.has('page')) return;
    next.delete('page');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const setPage = useCallback(
    (p: number) => {
      const next = new URLSearchParams(searchParams);
      if (p <= 1) next.delete('page');
      else next.set('page', String(p));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (prevDebouncedSearchRef.current === null) {
      prevDebouncedSearchRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedSearchRef.current !== debouncedSearch) {
      prevDebouncedSearchRef.current = debouncedSearch;
      clearPageFromUrl();
    }
  }, [debouncedSearch, clearPageFromUrl]);

  useEffect(() => {
    if (didInitFromParamsRef.current) return;
    const seriesId = searchParams.get('seriesId');
    const seriesName = searchParams.get('seriesName');
    if (seriesId) {
      setSeriesFilter({
        id: seriesId,
        name: seriesName ?? '',
        coverUrl: null,
        maxAmount: null,
        createdAt: '',
        updatedAt: '',
      });
    }
    didInitFromParamsRef.current = true;
  }, [searchParams]);

  const handleResetFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setPlacementFilter('');
    setReadStatusFilter('');
    setSeriesFilter(null);
    setSearchParams({});
  };

  const handleConfirmDelete = () => {
    const id = confirmDeleteId;
    if (!id) return;
    setDeletingId(id);
    setConfirmDeleteId(null);
    deleteBook(id)
      .then(() => load())
      .catch((err) => {
        setDeleteError(err instanceof Error ? err.message : 'Ошибка удаления');
      })
      .finally(() => setDeletingId(null));
  };

  const handleCloseDeleteError = () => {
    setDeleteError(null);
  };

  const handleSeriesChange = (value: Series | null) => {
    setSeriesFilter(value);
    const next = new URLSearchParams(searchParams);
    next.delete('page');
    if (value) {
      next.set('seriesId', value.id);
      next.set('seriesName', value.name);
    } else {
      next.delete('seriesId');
      next.delete('seriesName');
    }
    setSearchParams(next);
  };

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    getBooks({
      page,
      limit: PAGE_SIZE,
      q: debouncedSearch || undefined,
      placement: placementFilter || undefined,
      readStatus: readStatusFilter || undefined,
      seriesId: seriesFilter?.id || undefined,
    })
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        setData({ items: res.items, total: res.total, stats: res.stats });
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [page, debouncedSearch, placementFilter, readStatusFilter, seriesFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data || data.total === 0) return;
    const last = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
    if (page > last) setPage(last);
  }, [data, page, setPage]);

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(id);
  };

  if (loading && !data) {
    return (
      <Box className={styles.root}>
        <Typography color="text.secondary">Загрузка…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.root}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const stats = data?.stats;

  const handlePlacementChipClick = (placement: Placement) => {
    setPlacementFilter((prev) => (prev === placement ? '' : placement));
    clearPageFromUrl();
  };

  const handleReadStatusChipClick = (status: ReadStatus) => {
    setReadStatusFilter((prev) => (prev === status ? '' : status));
    clearPageFromUrl();
  };

  return (
    <Box className={styles.root}>
      <Box className={styles.header}>
        <Box className={styles.nav}>
          <Button
            variant="contained"
            size="small"
            className={styles.navButton}
            onClick={handleResetFilters}
          >
            Книги
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={styles.navButton}
            component={Link}
            to="/collections"
          >
            Коллекции
          </Button>
        </Box>
      </Box>

      <Box className={styles.toolbar}>
        <TextField
          fullWidth
          size="small"
          placeholder="Поиск по названию, автору, комментарию, циклу…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Поиск"
        />
        <Box className={styles.filters}>
          <FormControl size="small" className={styles.filter}>
            <InputLabel id="placement-label">Где</InputLabel>
            <Select
              labelId="placement-label"
              label="Где"
              value={placementFilter}
              onChange={(e) => {
                setPlacementFilter(e.target.value);
                clearPageFromUrl();
              }}
            >
              <MenuItem value="">Где угодно</MenuItem>
              <MenuItem value="collection">Коллекция</MenuItem>
              <MenuItem value="wishlist">Вишлист</MenuItem>
              <MenuItem value="in_transit">В пути</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" className={styles.filter}>
            <InputLabel id="status-label">Статус</InputLabel>
            <Select
              labelId="status-label"
              label="Статус"
              value={readStatusFilter}
              onChange={(e) => {
                setReadStatusFilter(e.target.value);
                clearPageFromUrl();
              }}
            >
              <MenuItem value="">Любой</MenuItem>
              <MenuItem value="not_started">Не начата</MenuItem>
              <MenuItem value="reading">Читаю</MenuItem>
              <MenuItem value="finished">Прочитана</MenuItem>
            </Select>
          </FormControl>
          <Box className={styles.filter}>
            <SeriesAutocomplete
              value={seriesFilter}
              onChange={handleSeriesChange}
              label="Цикл"
              size="small"
            />
          </Box>
        </Box>
        {total > 0 && stats != null && (
          <Box className={styles.statsSection}>
            <Typography variant="body2" color="text.secondary">
              {booksCountPhrase(total)} по выбранным фильтрам
            </Typography>
            <Box className={styles.statsChips}>
              <FilterPlacementChip
                placement="collection"
                count={stats.byPlacement.collection}
                label="В коллекции"
                color="success"
                selected={placementFilter === 'collection'}
                onClick={() => handlePlacementChipClick('collection')}
              />
              <FilterPlacementChip
                placement="wishlist"
                count={stats.byPlacement.wishlist}
                label="В вишлисте"
                color="warning"
                selected={placementFilter === 'wishlist'}
                onClick={() => handlePlacementChipClick('wishlist')}
              />
              <FilterPlacementChip
                placement="in_transit"
                count={stats.byPlacement.in_transit}
                label="В пути"
                color="info"
                selected={placementFilter === 'in_transit'}
                onClick={() => handlePlacementChipClick('in_transit')}
              />
              <FilterReadStatusChip
                count={stats.byReadStatus.not_started}
                label={READ_STATUS_LABELS.not_started}
                selected={readStatusFilter === 'not_started'}
                onClick={() => handleReadStatusChipClick('not_started')}
              />
              <FilterReadStatusChip
                count={stats.byReadStatus.reading}
                label={READ_STATUS_LABELS.reading}
                selected={readStatusFilter === 'reading'}
                onClick={() => handleReadStatusChipClick('reading')}
              />
              <FilterReadStatusChip
                count={stats.byReadStatus.finished}
                label={READ_STATUS_LABELS.finished}
                selected={readStatusFilter === 'finished'}
                onClick={() => handleReadStatusChipClick('finished')}
              />
            </Box>
            {stats.byReadStatus.unset > 0 && (
              <Typography variant="caption" color="text.secondary" className={styles.statsHint}>
                Без статуса чтения: {stats.byReadStatus.unset}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {items.length === 0 ? (
        <Typography color="text.secondary" className={styles.empty}>
          {debouncedSearch || placementFilter || readStatusFilter || seriesFilter
            ? 'Ничего не найдено. Измените поиск или фильтры.'
            : 'Книг пока нет. Добавьте первую.'}
        </Typography>
      ) : (
        <Box component="ul" className={styles.list}>
          {items.map((book) => (
            <Card key={book.id} component="li" className={styles.card}>
              <Link to={`/books/${book.id}/edit`} className={styles.cardLink}>
                <BookCover coverUrl={book.coverUrl} title={book.title} className={styles.cover} />
                <CardContent className={styles.cardContent}>
                  <Box className={styles.cardInner}>
                  <Typography variant="h6" component="h2" className={styles.cardTitle}>
                    {book.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {book.author.name}
                  </Typography>
                  <Box className={styles.chips}>
                    <Chip
                      size="small"
                      label={PLACEMENT_LABELS[book.placement]}
                      color={
                        book.placement === 'collection'
                          ? 'success'
                          : book.placement === 'wishlist'
                            ? 'warning'
                            : 'info'
                      }
                      variant="outlined"
                    />
                    {book.readStatus && (
                      <Chip
                        size="small"
                        label={READ_STATUS_LABELS[book.readStatus]}
                        color={book.readStatus === 'finished' ? 'primary' : 'default'}
                        variant={book.readStatus === 'finished' ? 'filled' : 'outlined'}
                      />
                    )}
                    {book.rating != null && (
                      <Chip size="small" label={`${book.rating} ★`} variant="outlined" />
                    )}
                    {book.series && (
                      <Chip
                        size="small"
                        label={
                          book.part != null
                            ? `${book.series.name}, часть ${book.part}`
                            : book.series.name
                        }
                        component={Link}
                        to={`/collections/${encodeURIComponent(book.series.id)}?name=${encodeURIComponent(book.series.name)}`}
                        clickable
                        variant="outlined"
                      />
                    )}
                    {book.comment && (
                      <Chip
                        size="small"
                        label={
                          book.comment.length > 40
                            ? `${book.comment.slice(0, 40)}…`
                            : book.comment
                        }
                        title={book.comment}
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
                </CardContent>
              </Link>
              <CardActions className={styles.cardActions}>
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={(e) => handleDelete(e, book.id)}
                  disabled={deletingId === book.id}
                  className={styles.deleteIconButton}
                  aria-label="Удалить книгу"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}
      <ListPagination
        page={page}
        pageCount={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        total={total}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
      <Button
        variant="contained"
        color="primary"
        component={Link}
        to="/books/new"
        className={styles.fab}
        aria-label="Добавить книгу"
      >
        <AddIcon />
      </Button>
      <Dialog
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
      >
        <DialogTitle>Удалить книгу?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить эту книгу? Это действие нельзя будет отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)} color="inherit">
            Отмена
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(deleteError)}
        autoHideDuration={6000}
        onClose={handleCloseDeleteError}
        message={deleteError ?? ''}
      />
    </Box>
  );
}

interface FilterPlacementChipProps {
  placement: Placement;
  count: number;
  label: string;
  color: 'success' | 'warning' | 'info';
  selected: boolean;
  onClick: () => void;
}

function FilterPlacementChip({
  count,
  label,
  color,
  selected,
  onClick,
}: FilterPlacementChipProps): ReactNode {
  if (!count) return null;
  return (
    <Chip
      size="small"
      label={`${label}: ${count}`}
      color={color}
      variant={selected ? 'filled' : 'outlined'}
      onClick={onClick}
      clickable
    />
  );
}

interface FilterReadStatusChipProps {
  count: number;
  label: string;
  selected: boolean;
  onClick: () => void;
}

function FilterReadStatusChip({
  count,
  label,
  selected,
  onClick,
}: FilterReadStatusChipProps): ReactNode {
  if (!count) return null;
  return (
    <Chip
      size="small"
      label={`${label}: ${count}`}
      color={selected ? 'primary' : 'default'}
      variant={selected ? 'filled' : 'outlined'}
      onClick={onClick}
      clickable
    />
  );
}
